import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

/**
 * Modal shown when the signed-in user still has must_change_password=true.
 * Cannot be dismissed — forces a password change before continuing.
 */
export default function MustChangePasswordDialog() {
  const { user, checkAuth } = useAuth();
  const open = !!user && user.must_change_password === true && user.role !== "candidate";
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error("New password must be at least 8 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      setCurrent(""); setNext(""); setConfirm("");
      await checkAuth();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Failed to change password");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => { /* blocked */ }}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} data-testid="force-change-pw-dialog">
        <DialogHeader>
          <div className="w-12 h-12 border border-gold flex items-center justify-center text-gold mb-2"><KeyRound size={20}/></div>
          <DialogTitle className="serif-display text-2xl">Set a new password</DialogTitle>
          <DialogDescription>
            For your security, please change the temporary password issued to you before continuing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Current (temporary) password</Label><PasswordInput value={current} onChange={(e)=>setCurrent(e.target.value)} data-testid="cpw-current" eyeTestid="cpw-current-eye"/></div>
          <div><Label>New password</Label><PasswordInput value={next} onChange={(e)=>setNext(e.target.value)} data-testid="cpw-new" eyeTestid="cpw-new-eye"/></div>
          <div><Label>Confirm new password</Label><PasswordInput value={confirm} onChange={(e)=>setConfirm(e.target.value)} data-testid="cpw-confirm" eyeTestid="cpw-confirm-eye"/></div>
          <DialogFooter>
            <button type="submit" className="btn-primary w-full justify-center" disabled={saving} data-testid="cpw-submit">{saving ? "Updating..." : "Update password"}</button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
